"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useOnboarding } from "@/onboarding/OnboardingContext";
import { saveDataTypes, saveDataPools } from "@/services/dataRetentionService";
import { resolveSystemIds } from "@/app/pages/data-retention-schedule/assetLinkingUtil";
import { getAssetsByType, updateAsset } from "@/services/assetsService";
import { toast } from "sonner";
import { Loader2, Check, Plus } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { DuckLoadingScreen } from "@/components/DuckLoadingScreen";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const SUGGESTED_TYPES = [
  { id: "contact", name: "Customer Contact Info", reason: "Found in CRM and Support vendors" },
  { id: "billing", name: "Billing & Payment", reason: "Found in Xero/Stripe vendors" },
  { id: "usage", name: "Product Usage Analytics", reason: "Found in analytics vendors" },
  { id: "employee", name: "Employee HR Records", reason: "Found in HR/payroll vendors" },
];

export default function ModalDataTypesSuggestExample() {
  const [open, setOpen] = React.useState(true);
  const [query, setQuery] = React.useState("");
  const [selected, setSelected] = React.useState<string[]>(SUGGESTED_TYPES.map(t => t.id));
  const [types, setTypes] = React.useState(SUGGESTED_TYPES);
  const [typeMeta, setTypeMeta] = React.useState<Record<string, { suppliers: string[]; systems: string[]; pools: string[] }>>({});
  const [saving, setSaving] = React.useState(false);
  const { setStatus, openStep, closeActive, goBack } = useOnboarding();
  
  // DED-1579: Add custom data type state
  const [showAddType, setShowAddType] = React.useState(false);
  const [newTypeName, setNewTypeName] = React.useState("");
  const [newTypeDescription, setNewTypeDescription] = React.useState("");
  const canAddType = newTypeName.trim().length > 2;

  const addCustomType = () => {
    if (!canAddType) return;
    
    const newId = `custom-${Date.now()}`;
    const newType = {
      id: newId,
      name: newTypeName.trim(),
      reason: newTypeDescription.trim() || 'Custom data type added by user'
    };
    
    // Add to types list
    setTypes(prev => [newType, ...prev]);
    
    // Add metadata
    setTypeMeta(prev => ({
      ...prev,
      [newId]: {
        suppliers: [],
        systems: [],
        pools: []
      }
    }));
    
    // Auto-select the new type
    setSelected(prev => [...prev, newId]);
    
    // Reset form
    setNewTypeName("");
    setNewTypeDescription("");
    setShowAddType(false);
    
    toast.success(`Added "${newType.name}" to data types`);
  };

  const handleSave = async () => {
    if (selected.length === 0) {
      toast.error("Please select at least one data type");
      return;
    }
    setSaving(true);
    try {
      const companyID = (window as any).companyID;
      if (!companyID) throw new Error("Company ID not found");

      // 1. Save Data Types
      const selectedItems = types.filter(t => selected.includes(t.id));
      const entities = selectedItems.map(t => ({
        dataTypeName: t.name,
        dataTypeJustification: t.reason,
        dataTypeModeOfCollection: 'Not specified',
        isPersonalInformation: false,
        deleted: false,
        dataTypeMetadata: {
          addedBy: 'Onboarding',
          dateAdded: new Date(),
          dateLastUpdated: new Date()
        }
      }));
      
      await saveDataTypes(entities as any);
      console.log('[DataTypes] Saved', entities.length, 'data types');
      
      try { 
        localStorage.setItem("onboardingAI.accepted.data_labels", JSON.stringify(selectedItems)); 
      } catch { }

      // 2. Update suppliers with their linked data types
      try {
        const raw = typeof window !== 'undefined' ? localStorage.getItem('onboardingAI.lastResponse') : null;
        if (raw) {
          const parsed = JSON.parse(raw);
          const payload = parsed?.data?.payload;
          const labelsArr: any[] = Array.isArray(payload?.data_labels) ? payload.data_labels : [];
          const poolsArr: any[] = Array.isArray(payload?.data_pools) ? payload.data_pools : [];
          const procsArr: any[] = Array.isArray(payload?.processes) ? payload.processes : [];
          const suppliersList: any[] = (payload?.query?.suppliers && Array.isArray(payload.query.suppliers)) 
            ? payload.query.suppliers 
            : (Array.isArray(parsed?.data?.query?.suppliers) ? parsed.data.query.suppliers : []);
          
          // Build a map of supplier name -> data type names
          const supplierDataTypes = new Map<string, Set<string>>();
          
          // For each supplier, find which data types are linked via processes -> pools -> labels
          for (const supplier of suppliersList) {
            const supplierName = supplier.name;
            const dataTypeNames = new Set<string>();
            
            // Find processes that use this supplier
            const supplierProcesses = procsArr.filter((pr: any) => 
              Array.isArray(pr?.supplier_ids) && pr.supplier_ids.includes(supplier.id)
            );
            
            // Get pool IDs from those processes
            const poolIds = new Set<string>(
              supplierProcesses.flatMap((pr: any) => 
                Array.isArray(pr?.data_pool_ids) ? pr.data_pool_ids : []
              )
            );
            
            // Get data label IDs from those pools
            for (const poolId of poolIds) {
              const pool = poolsArr.find((p: any) => p.id === poolId);
              if (pool && Array.isArray(pool.data_label_ids)) {
                for (const labelId of pool.data_label_ids) {
                  // Only include if this label was selected by the user
                  if (selected.includes(labelId)) {
                    const label = labelsArr.find((l: any) => (l.id || l.name) === labelId);
                    if (label) {
                      dataTypeNames.add(label.display_name || label.name);
                    }
                  }
                }
              }
            }
            
            if (dataTypeNames.size > 0) {
              supplierDataTypes.set(supplierName.toLowerCase(), dataTypeNames);
            }
          }
          
          // Now update the supplier assets with their data types
          if (supplierDataTypes.size > 0) {
            const supplierAssets = await getAssetsByType('supplier');
            console.log('[DataTypes] Found', supplierAssets.length, 'supplier assets to potentially update');
            
            for (const asset of supplierAssets) {
              const assetName = (asset.assetName || asset.assetDesc || '').toLowerCase();
              const dataTypes = supplierDataTypes.get(assetName);
              
              if (dataTypes && dataTypes.size > 0 && asset.assetID) {
                const dataTypesArray = Array.from(dataTypes);
                try {
                  await updateAsset(asset.assetID, {
                    dataTypesShared: dataTypesArray,
                    dataTypesSharedString: dataTypesArray.join(', ')
                  });
                  console.log(`[DataTypes] Updated supplier "${assetName}" with data types:`, dataTypesArray);
                } catch (err) {
                  console.error(`[DataTypes] Failed to update supplier "${assetName}":`, err);
                }
              }
            }
          }
        }
      } catch (supplierUpdateError) {
        console.error('[DataTypes] Failed to update suppliers with data types:', supplierUpdateError);
        // Don't fail the whole operation
      }

      // 3. Automatically save Data Pools (hidden step)
      try {
        const raw = typeof window !== 'undefined' ? localStorage.getItem('onboardingAI.lastResponse') : null;
        if (raw) {
          const parsed = JSON.parse(raw);
          const payload = parsed?.data?.payload;
          const poolItems = payload?.data_pools;
          
          if (Array.isArray(poolItems) && poolItems.length > 0) {
            const labelsArr: any[] = Array.isArray(payload?.data_labels) ? payload.data_labels : [];
            const procsArr: any[] = Array.isArray(payload?.processes) ? payload.processes : [];
            const suppliersList: any[] = (payload?.query?.suppliers && Array.isArray(payload.query.suppliers)) 
              ? payload.query.suppliers 
              : (Array.isArray(parsed?.data?.query?.suppliers) ? parsed.data.query.suppliers : []);
            const supplierNameById = new Map<string, string>(suppliersList.map((s: any) => [s.id, s.name]));
            const labelNameById = new Map<string, string>(labelsArr.map((l: any) => [l.id || l.name, l.display_name || l.name]));

            // Build pool entities with metadata
            const poolEntities = await Promise.all(poolItems.map(async (dp: any) => {
              const systems = (Array.isArray(dp?.primary_systems) ? Array.from(new Set(dp.primary_systems)) : []) as string[];
              const dataTypeIds = (Array.isArray(dp?.data_label_ids) ? Array.from(new Set(dp.data_label_ids)) : []) as string[];
              const labels = dataTypeIds.map((id: string) => labelNameById.get(id)).filter(Boolean) as string[];
              const supplierIds = (Array.from(new Set(
                procsArr
                  .filter((pr: any) => Array.isArray(pr?.data_pool_ids) && pr.data_pool_ids.includes(dp.id))
                  .flatMap((pr: any) => (Array.isArray(pr?.supplier_ids) ? pr.supplier_ids : []))
              )) as string[]);
              const supplierNames = supplierIds.map((sid: string) => supplierNameById.get(sid)).filter(Boolean) as string[];
              
              const systemIds = await resolveSystemIds(systems);
              
              return {
                id: dp.id || dp.name,
                name: dp.name,
                reason: dp.description || (systems.length ? `Systems: ${systems.join(', ')}` : 'Suggested'),
                dataTypes: dataTypeIds,
                systems: systemIds,
                suppliers: supplierNames,
                labels: Array.from(new Set(labels)) as string[],
                status: 'active' as const
              };
            }));

            await saveDataPools(companyID, poolEntities);
            
            try { 
              localStorage.setItem("onboardingAI.accepted.data_pools", JSON.stringify(poolItems)); 
            } catch { }
            
            console.log('[DataTypes] Auto-saved', poolEntities.length, 'data pools');
          }
        }
      } catch (poolError) {
        console.error('[DataTypes] Failed to auto-save data pools:', poolError);
        // Don't fail the whole operation if pools fail - they can be added later
      }
      
      toast.success(`${selectedItems.length} data types saved`);
      
      // Mark both steps as done and skip to processes
      setStatus("data_labels", "done");
      setStatus("data_pools", "done");
      setOpen(false);
      openStep("import_privacy_policy");
    } catch (error) {
      console.error("Failed to save data types", error);
      toast.error("Failed to save data types");
    } finally {
      setSaving(false);
    }
  };

  const filtered = React.useMemo(() => types.filter(t => t.name.toLowerCase().includes(query.toLowerCase())), [query, types]);
  const toggle = (id: string) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const allShownSelected = filtered.length > 0 && filtered.every(t => selected.includes(t.id));
  const toggleAll = () => {
    if (allShownSelected) {
      setSelected(prev => prev.filter(id => !filtered.some(t => t.id === id)));
    } else {
      setSelected(prev => Array.from(new Set([...prev, ...filtered.map(t => t.id)])));
    }
  };

  React.useEffect(() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem('onboardingAI.lastResponse') : null;
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const payload = parsed?.data?.payload;
      const items = payload?.data_labels;
      if (Array.isArray(items) && items.length > 0) {
        const mapped = items.map((d: any) => ({
          id: d.id || d.name,
          name: d.display_name || d.name,
          reason: d.description || (d.category ? `Category: ${d.category}` : 'Suggested'),
        }));
        setTypes(mapped);
        setSelected(mapped.map((m: any) => m.id));

        const poolsArr: any[] = Array.isArray(payload?.data_pools) ? payload.data_pools : [];
        const procsArr: any[] = Array.isArray(payload?.processes) ? payload.processes : [];
        const suppliersList: any[] = (payload?.query?.suppliers && Array.isArray(payload.query.suppliers)) ? payload.query.suppliers : (Array.isArray(parsed?.data?.query?.suppliers) ? parsed.data.query.suppliers : []);
        const supplierNameById = new Map<string, string>(suppliersList.map((s: any) => [s.id, s.name]));

        const meta: Record<string, { suppliers: string[]; systems: string[]; pools: string[] }> = {};
        mapped.forEach((m: any) => {
          const labelId = m.id;
          const containingPools = poolsArr.filter((dp: any) => Array.isArray(dp?.data_label_ids) && dp.data_label_ids.includes(labelId));
          const poolIds = new Set<string>(containingPools.map((p: any) => p.id));
          const systems = Array.from(new Set(containingPools.flatMap((p: any) => Array.isArray(p?.primary_systems) ? p.primary_systems : []).filter(Boolean))) as string[];
          const suppliers = Array.from(new Set(procsArr
            .filter((pr: any) => Array.isArray(pr?.data_pool_ids) && pr.data_pool_ids.some((id: string) => poolIds.has(id)))
            .flatMap((pr: any) => Array.isArray(pr?.supplier_ids) ? pr.supplier_ids : [])
          )) as string[];
          const supplierNames = suppliers.map((sid: string) => supplierNameById.get(sid)).filter(Boolean) as string[];
          const poolNames = containingPools.map((p: any) => p.name).filter(Boolean);
          meta[labelId] = {
            suppliers: supplierNames,
            systems,
            pools: Array.from(new Set(poolNames)),
          };
        });
        setTypeMeta(meta);
      }
    } catch { }
  }, []);

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open Data Types Suggest</Button>
      <Dialog open={open} onOpenChange={(v) => { if (!saving) { setOpen(v); if (!v) closeActive(); } }}>
        <DialogContent className="max-w-7xl! w-7xl! h-screen bg-transparent shadow-none border-0 p-0 flex items-center justify-center">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
            {/* Close button */}
            <button
              onClick={() => { if (!saving) { setOpen(false); closeActive(); } }}
              className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Close"
              disabled={saving}
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
            </button>
            
            {/* Duck Loading Screen Overlay */}
            {saving && (
              <div className="absolute inset-0 z-50 rounded-xl overflow-hidden">
                <DuckLoadingScreen 
                  variant="overlay"
                  title="Saving Data Types" 
                  text="Saving your selected data types and adding them to your Data Register"
                  duckFacts={[
                    "Quack! Saving your data types...",
                    "Automatically creating data pools...",
                    "Linking systems and suppliers...",
                    "Almost there! Finalising your data structure...",
                    "Duck is organising your data ecosystem!"
                  ]}
                />
              </div>
            )}
            
            <div className="p-6 pb-4 border-b border-white/10 bg-linear-to-b from-primary/5 to-transparent">
              <h2 className="text-3xl font-bold text-foreground">Suggested Data Types</h2>
              <p className="mt-2 text-muted-foreground">Based on your suppliers and processes, we suggest the following data types are in use by your business.</p>
              <p className="mt-1 text-sm text-muted-foreground/80">Please deselect any that don't apply to your business.</p>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                  <Input placeholder="Search data types..." value={query} onChange={(e) => setQuery(e.target.value)} className="bg-white/10 border-white/20 sm:max-w-sm" />
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <button className="hover:underline" type="button" onClick={toggleAll}>{allShownSelected ? 'Deselect shown' : 'Select shown'}</button>
                    <span>|</span>
                    <span>{selected.length} selected</span>
                  </div>
                </div>
                {/* DED-1579: Add Custom Data Type button */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowAddType(!showAddType)}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Custom Data Type
                </Button>
              </div>

              {/* DED-1579: Add Custom Data Type Form */}
              {showAddType && (
                <div className="rounded-xl border-2 border-dashed border-primary/50 bg-primary/5 p-4 space-y-4">
                  <div className="flex items-center gap-2 text-primary font-medium">
                    <Plus className="h-5 w-5" />
                    Add Your Own Data Type
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="typeName">Data Type Name *</Label>
                      <Input 
                        id="typeName"
                        placeholder="e.g., Customer Preferences" 
                        value={newTypeName} 
                        onChange={(e) => setNewTypeName(e.target.value)} 
                        className="bg-white/10 border-white/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="typeDesc">Description (optional)</Label>
                      <Input 
                        id="typeDesc"
                        placeholder="e.g., User preferences and settings"
                        value={newTypeDescription}
                        onChange={(e) => setNewTypeDescription(e.target.value)}
                        className="bg-white/10 border-white/20"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => {
                      setShowAddType(false);
                      setNewTypeName("");
                      setNewTypeDescription("");
                    }}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={addCustomType} disabled={!canAddType}>
                      Add Data Type
                    </Button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.map(t => {
                  const isSelected = selected.includes(t.id);
                  const isCustom = t.id.startsWith('custom-');
                  return (
                    <div
                      key={t.id}
                      onClick={() => toggle(t.id)}
                      style={{
                        background: isSelected ? 'linear-gradient(135deg, rgba(var(--primary-rgb), 0.15), rgba(var(--primary-rgb), 0.05))' : '',
                        backdropFilter: 'blur(8px)'
                      }}
                      className={`relative overflow-hidden rounded-xl p-4 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                        isSelected
                          ? 'border-2 border-primary shadow-lg'
                          : 'bg-white/5 hover:bg-white/10 dark:bg-gray-800/30 dark:hover:bg-gray-800/50 border border-white/10 shadow-sm hover:shadow-md'
                      }`}
                    >
                      {/* Light accent when selected */}
                      {isSelected && (
                        <div className="absolute inset-0 bg-linear-to-tr from-primary/5 via-transparent to-primary/10 pointer-events-none" />
                      )}
                      
                      {/* Selection indicator */}
                      {isSelected && (
                        <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-primary flex items-center justify-center shadow-sm z-20">
                          <Check className="h-3 w-3 text-primary-foreground" />
                        </div>
                      )}
                      
                      {/* DED-1579: Custom badge */}
                      {isCustom && (
                        <Badge variant="secondary" className="absolute top-3 left-3 text-[10px]">Custom</Badge>
                      )}
                      
                      <div className="relative z-10">
                        <div className="text-foreground font-medium">{t.name}</div>
                        <div className="text-xs text-muted-foreground mt-1">{t.reason}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* {selected.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selected.map(id => {
                    const t = types.find(x => x.id === id);
                    if (!t) return null;
                    return <Badge key={id} variant="outline" className="text-xs">{t.name}</Badge>
                  })}
                </div>
              )} */}
            </div>

            <div className="border-t border-white/10 px-6 py-4 bg-linear-to-t from-primary/5 to-transparent flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setOpen(false); goBack(); }}>Back</Button>
              <Button onClick={handleSave} disabled={saving}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Accept & Next
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
